import React from 'react'
import styled from 'styled-components'
import { Controller, FormContextValues } from 'react-hook-form'

import Scale from '../../components/Scales/Scale'
import { ID } from '../../types/id'
import { InquiryStatus, Inquiries, disabilityLevel } from './utils'
import { InquiryFormType } from '../../utils/constants'
import {
  ageInMonths,
  getSpeLevelFromExaminationCode,
} from '../../utils/patientMisc'
import { inquiryForm } from '../../utils/constants'
import { ErrorMessage } from './ErrorMessage/ErrorMessage'
import { validateEmail } from '../../utils/validation'

import { Status } from './Status'
// @ts-ignore it's about config
import formIcon from './form.svg'
import { Hidden } from '../../styles/Shared'
import colors from '../../styles/colors'
import WithFocus from '../../components/WithFocus'
import Button from '../../components/Button'

type Props = {
  onAbort: () => void
  onSubmit: () => void
  form: FormContextValues<Inquiries>
  inquiry?: Inquiries
  code: string
}

const sendBlocked = (status: InquiryStatus | null = 'NOT_ORDERED'): boolean =>
  status === 'ANSWERED_BY_LINK' || status === 'ANSWERED_ON_BEHALF'

const mentalDisabilitySelectBlocked = (inquiry?: Inquiries): boolean => {
  const disablingStatuses = [
    'PENDING_DELIVERY',
    'DELIVERED',
    'ANSWERED_BY_LINK',
    'ANSWERED_ON_BEHALF',
    'SENT',
  ] as const

  if (
    disablingStatuses.some(
      (status) => status === (inquiry?.parent1?.inquiryStatus ?? 'NOT_ORDERED')
    ) ||
    disablingStatuses.some(
      (status) => status === (inquiry?.parent2?.inquiryStatus ?? 'NOT_ORDERED')
    ) ||
    disablingStatuses.some(
      (status) => status === (inquiry?.tutor1?.inquiryStatus ?? 'NOT_ORDERED')
    ) ||
    disablingStatuses.some(
      (status) => status === (inquiry?.tutor2?.inquiryStatus ?? 'NOT_ORDERED')
    )
  ) {
    return true
  }
  return false
}

type State = {
  id: ID
  code: string
  status: InquiryStatus
  examinationId: ID
  formType: InquiryFormType['name']
} | null

export function InquiryForm({ onSubmit, onAbort, form, inquiry, code }: Props) {
  const speLevel = getSpeLevelFromExaminationCode(code)
  const [activeForm, setActiveForm] = React.useState<State>(null)
  const isSpeGroupOne = speLevel === 1
  const { control, formState, watch, errors } = form
  const formValues = watch()
  const scaleByMentalDisabilityLevel = React.useMemo(
    () =>
      inquiryForm.filter((scale) => {
        switch (formValues.mentalLevel) {
          case 'Głęboki':
            return scale.name.includes('KAPP_SNIG')
          case 'Umiarkowany':
            return scale.name.includes('KAPP_SNIU')
          case 'Znaczny':
            return scale.name.includes('KAPP_SNIZ')
          default:
            return scale
        }
      }),
    [formValues.mentalLevel]
  )
  const filteredScales = (isSpeGroupOne
    ? scaleByMentalDisabilityLevel
    : inquiryForm
  )
    .filter(
      (scale) =>
        scale.minAgeInMonths < ageInMonths(code) &&
        scale.maxAgeInMonths >= ageInMonths(code) &&
        scale.speLevel.includes(speLevel)
    )
    .map((scale) => scale.name)
  const parentScales = filteredScales.filter((name) => name.endsWith('(R)'))
  const tutorScales = filteredScales.filter((name) => name.endsWith('(N)'))

  return (
    <Panel>
      <FormWrapper onSubmit={onSubmit} activeForm={activeForm}>
        {/* parent1 */}
        {isSpeGroupOne && (
          <DisabilityLevelField>
            <label htmlFor="disability-level">
              Poziom niepełnosprawności
              <select
                id="disability-level"
                ref={form.register}
                name="mentalLevel"
                disabled={mentalDisabilitySelectBlocked(inquiry)}
              >
                <option value="" />
                {disabilityLevel.map((option) => (
                  <option key={option}>{option}</option>
                ))}
              </select>
              {errors?.mentalLevel && (
                <ErrorMessage>{errors.mentalLevel.message}</ErrorMessage>
              )}
            </label>
          </DisabilityLevelField>
        )}
        <Field>
          <label>
            Adres email rodzic 1
            <input
              ref={form.register}
              type="text"
              name="parent1.email"
              readOnly={
                sendBlocked(formValues?.['parent1.inquiryStatus']) ||
                (inquiry && inquiry.parent1Email !== null)
              }
            />
            {errors?.parent1?.email && (
              <ErrorMessage>{errors.parent1.email.message}</ErrorMessage>
            )}
          </label>
        </Field>
        <Field>
          <label htmlFor="parent-1-choose-scale">
            Wybierz skalę
            <select
              id="parent-1-choose-scale"
              disabled={sendBlocked(formValues?.['parent1.inquiryStatus'])}
              ref={form.register}
              name="parent1.type"
            >
              <option value="">Brak</option>
              {parentScales.map((option) => (
                <option key={option}>{option}</option>
              ))}
            </select>
          </label>
          {errors?.parent1?.type && (
            <ErrorMessage>{errors.parent1.type.message}</ErrorMessage>
          )}
        </Field>
        <Field>
          <label>
            Wysłać?
            <input
              ref={form.register}
              type="checkbox"
              name="parent1.send"
              disabled={
                sendBlocked(formValues?.['parent1.inquiryStatus']) ||
                formValues?.['parent1.type'] === '' ||
                !formValues['parent1.type']
              }
            />
          </label>
        </Field>
        <Controller
          control={control}
          name="parent1.inquiryStatus"
          as={<Status />}
        />
        <FillFormAction
          isSpeGroupOne={isSpeGroupOne}
          type="button"
          onClick={() =>
            setActiveForm(
              activeForm?.id === inquiry?.parent1?.id
                ? null
                : {
                    id: inquiry?.parent1?.id,
                    code: inquiry?.parent1?.code,
                    status: inquiry?.parent1.inquiryStatus,
                    examinationId: inquiry?.examinationId,
                    formType: inquiry?.parent1?.type,
                  }
            )
          }
          disabled={
            !inquiry?.parent1 ||
            inquiry?.parent1?.type === '' ||
            !inquiry.parent1.type
          }
        >
          <Hidden>Rozpocznij ankietę</Hidden>
          <img src={formIcon} alt="" aria-hidden="true" />
        </FillFormAction>
        {/* parent2 */}
        <Field>
          <label>
            Adres email rodzic 2
            <input
              ref={form.register}
              type="text"
              name="parent2.email"
              readOnly={
                sendBlocked(formValues?.['parent2.inquiryStatus']) ||
                (inquiry && inquiry.parent2Email !== null)
              }
            />
          </label>
          {errors?.parent2?.email && (
            <ErrorMessage>{errors.parent2.email.message}</ErrorMessage>
          )}
        </Field>
        <Field>
          <label htmlFor="parent-2-choose-scale">
            Wybierz skalę
            <select
              id="parent-2-choose-scale"
              ref={form.register}
              name="parent2.type"
              disabled={sendBlocked(formValues?.['parent2.inquiryStatus'])}
            >
              <option value="">Brak</option>
              {parentScales.map((option) => (
                <option key={option}>{option}</option>
              ))}
            </select>
          </label>
          {errors?.parent2?.type && (
            <ErrorMessage>{errors.parent2.type.message}</ErrorMessage>
          )}
        </Field>
        <Field>
          <label>
            Wysłać?
            <input
              ref={form.register}
              disabled={
                sendBlocked(formValues?.['parent2.inquiryStatus']) ||
                formValues?.['parent2.type'] === '' ||
                !formValues['parent2.type']
              }
              type="checkbox"
              name="parent2.send"
            />
          </label>
        </Field>
        <Controller
          control={control}
          name="parent2.inquiryStatus"
          as={<Status />}
        />
        <FillFormAction
          isSpeGroupOne={isSpeGroupOne}
          type="button"
          onClick={() =>
            setActiveForm(
              activeForm?.id === inquiry?.parent2?.id
                ? null
                : {
                    id: inquiry?.parent2?.id,
                    code: inquiry?.parent2?.code,
                    status: inquiry?.parent2?.inquiryStatus,
                    examinationId: inquiry?.examinationId,
                    formType: inquiry?.parent2?.type,
                  }
            )
          }
          disabled={
            !inquiry?.parent2 ||
            inquiry?.parent2?.type === '' ||
            !inquiry.parent2.type
          }
        >
          <Hidden>Rozpocznij ankietę</Hidden>
          <img src={formIcon} alt="" aria-hidden="true" />
        </FillFormAction>
        {/* tutor1 */}
        <Field>
          <label>
            Adres email nauczyciel 1
            <input
              ref={form.register}
              type="text"
              name="tutor1.email"
              readOnly={
                sendBlocked(formValues?.['tutor1.inquiryStatus']) ||
                (inquiry && inquiry.tutor1Email !== null)
              }
            />
          </label>
          {errors?.tutor1?.email && (
            <ErrorMessage>{errors.tutor1.email.message}</ErrorMessage>
          )}
        </Field>
        <Field>
          <label htmlFor="tutor-1-choose-scale">
            Wybierz skalę
            <select
              id="tutor-1-choose-scale"
              disabled={sendBlocked(formValues?.['tutor1.inquiryStatus'])}
              ref={form.register}
              name="tutor1.type"
            >
              <option value="">Brak</option>
              {tutorScales.map((option) => (
                <option key={option}>{option}</option>
              ))}
            </select>
          </label>
          {errors?.tutor1?.type && (
            <ErrorMessage>{errors.tutor1.type.message}</ErrorMessage>
          )}
        </Field>
        <Field>
          <label>
            Wysłać?
            <input
              ref={form.register}
              disabled={
                sendBlocked(formValues?.['tutor1.inquiryStatus']) ||
                formValues?.['tutor1.type'] === '' ||
                !formValues['tutor1.type']
              }
              type="checkbox"
              name="tutor1.send"
            />
          </label>
        </Field>
        <Controller
          control={control}
          name="tutor1.inquiryStatus"
          as={<Status />}
        />
        <FillFormAction
          isSpeGroupOne={isSpeGroupOne}
          onClick={() =>
            setActiveForm(
              activeForm?.id === inquiry?.tutor1?.id
                ? null
                : {
                    id: inquiry?.tutor1?.id,
                    code: inquiry?.tutor1?.code,
                    status: inquiry?.tutor1?.inquiryStatus,
                    examinationId: inquiry?.examinationId,
                    formType: inquiry?.tutor1?.type,
                  }
            )
          }
          type="button"
          disabled={
            !inquiry?.tutor1 ||
            inquiry?.tutor1?.type === '' ||
            !inquiry.tutor1.type
          }
        >
          <Hidden>Rozpocznij ankietę</Hidden>
          <img src={formIcon} alt="" aria-hidden="true" />
        </FillFormAction>
        {/* tutor2 */}
        <Field>
          <label>
            Adres email nauczyciel 2
            <input
              ref={form.register}
              type="text"
              name="tutor2.email"
              readOnly={
                sendBlocked(formValues?.['tutor2.inquiryStatus']) ||
                (inquiry && inquiry.tutor2Email !== null)
              }
            />
          </label>
          {errors?.tutor2?.email && (
            <ErrorMessage>{errors.tutor2.email.message}</ErrorMessage>
          )}
        </Field>
        <Field>
          <label htmlFor="tutor-2-choose-scale">
            Wybierz skalę
            <select
              id="tutor-2-choose-scale"
              ref={form.register}
              name="tutor2.type"
              disabled={sendBlocked(formValues?.['tutor2.inquiryStatus'])}
            >
              <option value="">Brak</option>
              {tutorScales.map((option) => (
                <option key={option}>{option}</option>
              ))}
            </select>
          </label>
          {errors?.tutor2?.type && (
            <ErrorMessage>{errors.tutor2.type.message}</ErrorMessage>
          )}
        </Field>
        <Field>
          <label>
            Wysłać?
            <input
              ref={form.register}
              disabled={
                sendBlocked(formValues?.['tutor2.inquiryStatus']) ||
                formValues?.['tutor2.type'] === '' ||
                !formValues['tutor2.type']
              }
              type="checkbox"
              name="tutor2.send"
            />
          </label>
        </Field>
        <Controller
          control={control}
          name="tutor2.inquiryStatus"
          as={<Status />}
        />
        <FillFormAction
          isSpeGroupOne={isSpeGroupOne}
          type="button"
          onClick={() =>
            setActiveForm(
              activeForm?.id === inquiry?.tutor2?.id
                ? null
                : {
                    id: inquiry?.tutor2?.id,
                    code: inquiry?.tutor2?.code,
                    status: inquiry?.tutor2?.inquiryStatus,
                    examinationId: inquiry?.examinationId,
                    formType: inquiry?.tutor2?.type,
                  }
            )
          }
          disabled={
            !inquiry?.tutor2 ||
            inquiry?.tutor2?.type === '' ||
            !inquiry.tutor2.type
          }
        >
          <Hidden>Rozpocznij ankietę</Hidden>
          <img src={formIcon} alt="" aria-hidden="true" />
        </FillFormAction>
        <SubmitButton type="submit" disabled={!formState.dirty}>
          Zapisz
        </SubmitButton>
        <button type="reset" onClick={onAbort}>
          Zamknij
        </button>
      </FormWrapper>
      {activeForm && (
        <WithFocus overflow="auto">
          <ScaleWrapper>
            <Scale
              id={activeForm.id}
              code={activeForm.code}
              speLevel={speLevel}
              isDiagnostician
              isEdition={activeForm.status === 'ANSWERED_ON_BEHALF'}
              examinationId={activeForm.examinationId}
              formType={activeForm.formType}
            />
          </ScaleWrapper>
          <WrapperScaleCloseButton>
            <Button
              tabIndex="2"
              label="Zamknij"
              onClick={() => setActiveForm(null)}
              width="auto"
            />
          </WrapperScaleCloseButton>
        </WithFocus>
      )}
    </Panel>
  )
}

const WrapperScaleCloseButton = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  margin-bottom: 10px;
`

const Panel = styled.div`
  display: flex;
  overflow: auto;
`

const ScaleWrapper = styled.div`
  display: flex;
  /* overflow-y: auto; */

  flex: 1 0;
`

const FillFormAction = styled.button`
  cursor: pointer;
  padding: 0;
  color: var(--color-primary);
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  background-color: transparent;
  font-weight: 500;
  border: 1px solid transparent;
  height: auto;
  grid-column: 3 /3;

  :nth-of-type(1) {
    ${(props) => (props.isSpeGroupOne ? `grid-row: 1 /5` : `grid-row: 1 /3`)};
  }

  :nth-of-type(2) {
    ${(props) => (props.isSpeGroupOne ? `grid-row: 3 /7` : `grid-row: 3 /5`)};
  }
  :nth-of-type(3) {
    ${(props) => (props.isSpeGroupOne ? `grid-row: 5 /9` : `grid-row: 5 /7`)};
  }
  :nth-of-type(4) {
    ${(props) => (props.isSpeGroupOne ? `grid-row: 7 /11` : `grid-row: 7 /9`)};
  }
`
const DisabilityLevelField = styled.div`
  display: flex;
  position: relative;
  align-items: center;
  justify-content: left;
  grid-column: 2/3;
  label,
  select {
    width: 100%;
  }
  input[type='checkbox'] {
    margin: 8px;
  }
`
const Field = styled.div`
  display: flex;
  position: relative;
  align-items: center;
  justify-content: left;
  label,
  select {
    width: 100%;
  }
  input[type='text'] {
    width: 100%;
  }
  input[type='checkbox'] {
    margin: 8px;
  }
  input:read-only {
    background: ${colors.readOnly};
    border-color: ${colors.readOnly};
  }
`

const SubmitButton = styled.button`
  border-radius: 3px;
  border: none;
  background: var(--color-primary);
  color: var(--color-text);
  grid-column: 1 /1;
  &:disabled {
    opacity: 0.6;
    color: black; // kolor dostosowany tak, by zapewnic wymagany poziom kontrastu WCAG
    background-color: ${colors.readOnly};
    cursor: auto;
  }
`
const FormWrapper = styled.form`
  padding: 1rem;
  display: grid;
  grid-template-columns: 13rem 13rem 2rem;
  grid-row-gap: 1rem;
  grid-column-gap: 2rem;
  grid-auto-rows: 3rem;
  @media (max-width: 1400px) {
    ${(props) => (props.activeForm ? `transform: translate3d(-300%, 0px, 0px)` : `transform: translate3d(0%, 0px, 0px)`)};
    ${(props) => (props.activeForm ? `width: 150px` : `width: auto`)};
  }
  label {
    margin: 0;
  }
  &:hover {
    transform: translate3d(0%, 0px, 0px);
    width: auto;
  }
`
